// Chassis-wide constants for the Unified Feedback Inbox. See
// veda:projects/unified-feedback-inbox/docs/11-chassis-plan.md §2 PR 01 and
// docs/06-ux-rules.md for the values these encode.

export { DEFAULT_PAGE_SIZE } from 'components/TablePaginationFooter'

// Right rail's "Recent feedback" panel: first N rows shown before
// "View %{count} more" (pin #1131).
export const RECENT_FEEDBACK_PREVIEW_COUNT = 5

// Three-pane layout grid (docs/06-ux-rules.md "Layout" / P10 D1).
export const LIST_PANE_WIDTH_PX = 300
export const RAIL_WIDTH_PX = 340

// Page chrome — white card on a grey page background (not in the ux-rules
// doc; ported from prototype/v69's `.inbox3` visual shell, index.html:435).
// `INBOX_PAGE_PADDING_PX` is the gutter between the card and InboxLayout's
// own edge — a chassis choice, not a literal prototype value (the
// prototype's spacing is page-level, outside this component's box).
export const INBOX_PAGE_PADDING_PX = 24
export const INBOX_CARD_BORDER_RADIUS_PX = 12
export const INBOX_BORDER_WIDTH_PX = 1

// Gap between the filter bar card and the panes card below it, now that
// the filter bar is its own floating card rather than living inside the
// panes card (prototype/v69's `.f-topbar` sits above `.inbox3` with a
// visible gap, index.html:1153).
export const INBOX_CARD_GAP_PX = 14

// Height floor for the three-pane card (docs/06-ux-rules.md "Height model" /
// P10 D2). 640 leaves a usable list of ~4-5 rows plus the thread and composer
// on a 13" laptop (~856px viewport, where page chrome takes ~290px). Tall
// screens still fill the viewport; below this the page scrolls rather than
// the panes getting shorter.
// Shared by both inbox hosts: the RecoveryAI Landing Escalations tab and the
// OrderFeedback page. Trade-off: on a ~856px laptop viewport the page scrolls
// about 200px instead of squashing the panes.
export const INBOX_PANES_MIN_HEIGHT_PX = 640

// Responsive floor (docs/06-ux-rules.md "Responsive floor" / P10 D3):
// below this, the layout renders an unsupported-viewport note instead of
// the grid. The >=1200 desktop threshold reuses `utilities/responsive.ts`'s
// existing `useIsDesktop()` (medium breakpoint 1199 + 1).
export const NARROW_VIEWPORT_MAX_WIDTH_PX = 899

// Resolve micro-interaction timings (docs/06-ux-rules.md "Resolve animation",
// R2/Q5 — exact numbers from prototype/v69's `resolveFade` /
// `resolveCheckPop` / `resolveRowExit` / `resPlus1` keyframes).
export const RESOLVE_WASH_MS = 1500
export const RESOLVE_CHECK_POP_MS = 500
export const RESOLVE_HOLD_MS = 650
export const RESOLVE_SWIPE_MS = 700
export const RESOLVE_ROW_HIDE_MS = 820
// Row-exit fade during the swipe (index.html's `resolveRowExit()`
// transition list: `opacity .8s ease`) — distinct from RESOLVE_ROW_HIDE_MS,
// which is when the JS actually unmounts the row (a 20ms buffer after this
// fade visually completes).
export const RESOLVE_EXIT_FADE_MS = 800
// Height/padding collapse once the swipe has had a moment to read
// (`resolveRowExit()`: `max-height .42s ease .34s, padding .42s ease .34s`).
export const RESOLVE_COLLAPSE_MS = 420
export const RESOLVE_COLLAPSE_DELAY_MS = 340
// The resolved status line's fade-up while the row flashes in place (v83
// `.ib-item.resolved-flash .ib-it-status.ok{animation:resolveStatusIn .55s
// ease-out both}`).
export const RESOLVE_STATUS_IN_MS = 550
// Green wash opacities (v83): the in-place flash starts at 0.18 and fades
// to 0 (`@keyframes resolveFade`); the leaving row holds a static 0.15
// (`.ib-item.resolve-exit::after`).
export const RESOLVE_FLASH_WASH_OPACITY = 0.18
export const RESOLVE_EXIT_WASH_OPACITY = 0.15

// List pane search box debounce (docs/06-ux-rules.md "List" / 11-chassis-
// plan.md §2 PR 04: "debounced 300ms → q").
export const SEARCH_DEBOUNCE_MS = 300

// The filter bar's "+1" pulse on the Resolved segment: fired at
// RESOLVE_SWIPE_MS during an in-place flash, or right after a leaving row
// exits (docs/06-ux-rules.md "Resolve animation" / prototype/v69's
// `resPlus1` keyframe, index.html:1375 — 1.9s total pop-in/hold/fade-out).
export const RESOLVED_PULSE_DURATION_MS = 1900

// Composer safety valve (docs/06-ux-rules.md "Composer" / "Panel") — caps
// the composer at a fraction of the thread pane's own height so a short
// viewport scrolls WITHIN the composer (`overflowY: auto`) instead of
// pushing Send below the pane's visible frame.
export const COMPOSER_MAX_HEIGHT_PCT = 60

// Thread pane item width (docs/06-ux-rules.md "Thread" / "Bubble/card
// width": "Fixed max-width regardless of message length"; P5 D6).
export const THREAD_ITEM_MAX_WIDTH_PX = 420
// SystemNote/ThreadNote are plain text rows, not bubbles/cards — they're
// intentionally wider than THREAD_ITEM_MAX_WIDTH_PX.
export const THREAD_NOTE_MAX_WIDTH_PX = 520

// The RecoveryAI wordmark image's rendered height in EmailCard's header bar
// (v83 `.cv-rai-wm svg{height:15px}`). Kept separate from SystemNote's own
// constant below — the two values differ.
export const EMAIL_CARD_WORDMARK_HEIGHT_PX = 15
// The RecoveryAI wordmark image's rendered height in SystemNote's escalation
// label row (v70).
export const SYSTEM_NOTE_WORDMARK_HEIGHT_PX = 12
